import { IProduct } from '@common/interface/product'
import MetaPixelMicrodataTag from '@components/meta-pixel-microdata-tag'
import ProductShopDetail from '@components/product-detail'
import MainLayout from '@layouts/main-layout'
import { medusaClient } from '@lib/config'
import { IS_BROWSER } from '@lib/constants'
import { fetchProduct } from '@lib/data'
import { getStructuredProduct } from '@lib/google-shopping'
import { getProductHandles } from '@lib/util/get-product-handles'
import GoogleShoppingHead from '@modules/common/components/google-shopping-head'
import Head from '@modules/common/components/head'
import SkeletonProductPage from '@modules/skeletons/templates/skeleton-product-page'
import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { ReactElement } from 'react'
import { NextPageWithLayout, PrefetchedPageProps } from 'types/global'

interface Params extends ParsedUrlQuery {
  handle: string
}

const ProductPage: NextPageWithLayout<PrefetchedPageProps> = ({ notFound }) => {
  const { query, isFallback, replace } = useRouter()
  const handle = typeof query.handle === 'string' ? query.handle : ''

  const { data, isError, isLoading, isSuccess } = useQuery(
    [`get_product`, handle],
    () => fetchProduct(handle),
    {
      enabled: handle.length > 0,
      keepPreviousData: true
    }
  )

  if (notFound) {
    if (IS_BROWSER) {
      replace('/404')
    }

    return <SkeletonProductPage />
  }

  if (isFallback || isLoading || !data) {
    return <SkeletonProductPage />
  }

  if (isError) {
    replace('/404')
  }

  if (isSuccess) {
    return (
      <>
        <Head
          description={data.description}
          title={data.title}
          image={data.thumbnail}
          link={
            data.handle
              ? `${
                  process.env.NEXT_PUBLIC_FRONTEND_DOMAIN as string
                }/products/${data.handle}`
              : undefined
          }
        />
        <GoogleShoppingHead
          structuredData={getStructuredProduct({
            product: data
          })}
        />
        <MetaPixelMicrodataTag item={data} />
        <ProductShopDetail product={data} isLoading={isLoading} />
      </>
    )
  }

  return <></>
}

ProductPage.getLayout = (page: ReactElement) => {
  return <MainLayout notRenderHead>{page}</MainLayout>
}

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const handles = await getProductHandles()
  return {
    paths: handles.map((handle) => ({ params: { handle } })),
    fallback: true
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const handle = context.params?.handle as string
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 1,
        retry: 1
      }
    }
  })

  await queryClient.prefetchQuery([`get_product`, handle], () =>
    fetchProduct(handle)
  )

  const queryData = await queryClient.getQueryData([`get_product`, handle])

  if (!queryData) {
    return {
      props: {
        notFound: true
      }
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      notFound: false,
      revalidate: 10
    }
  }
}

export default ProductPage
